import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Loader2, PenTool } from 'lucide-react';

interface SignatureContext {
  bookingReference: string;
  customerName: string;
  vehicle: string;
  jobId: string | null;
}

export function SignaturePage() {
  const { token } = useParams();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [context, setContext] = useState<SignatureContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const loadContext = async () => {
      if (!token) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/public/webhook/signature/${token}`);
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'No se pudo cargar la firma');
        }
        setContext(data.data as SignatureContext);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar la firma');
      } finally {
        setIsLoading(false);
      }
    };

    loadContext();
  }, [token]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const ratio = window.devicePixelRatio || 1;
    const displayWidth = canvas.offsetWidth || 600;
    const displayHeight = 220;

    canvas.width = displayWidth * ratio;
    canvas.height = displayHeight * ratio;
    canvas.style.height = `${displayHeight}px`;
    canvas.style.width = '100%';

    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111827';
  }, []);

  const getPoint = (event: PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const handlePointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPoint(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPoint(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const handlePointerUp = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const submitSignature = async () => {
    if (!token || !canvasRef.current) return;
    if (!hasSignature) {
      setError('Por favor, firma antes de enviar.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const signatureDataUrl = canvasRef.current.toDataURL('image/png');
      const response = await fetch(`/api/public/webhook/signature/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signatureDataUrl }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'No se pudo guardar la firma');
      }
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar la firma');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
        Cargando firma...
      </div>
    );
  }

  if (error && !context) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {error}
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center">
          <h1 className="text-lg font-semibold text-emerald-700">Firma recibida</h1>
          <p className="text-sm text-emerald-700 mt-2">
            Gracias. Hemos registrado tu firma de entrega.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="rounded-xl border bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <PenTool className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Firma de entrega</h1>
              {context && (
                <p className="text-sm text-gray-500">
                  {context.vehicle} · Ref {context.bookingReference}
                </p>
              )}
            </div>
          </div>

          <p className="text-sm text-gray-600 mt-4">
            Para finalizar la entrega, firma dentro del recuadro. Puedes usar el dedo o el mouse.
          </p>

          <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-white">
            <canvas
              ref={canvasRef}
              className="w-full touch-none"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            />
          </div>

          {error && (
            <div className="mt-3 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={clearSignature} disabled={isSubmitting}>
              Limpiar
            </Button>
            <Button onClick={submitSignature} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enviar firma
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignaturePage;
